import React, { useState, useRef } from 'react';
import api from '../api';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface ProductImage {
  id: number;
  s3_key?: string;
  image_url: string;
  original_filename?: string;
  image_type: string;
  alt_text?: string;
  file_size?: number;
  content_type?: string;
  sort_order: number;
  is_primary: boolean;
  created_at?: string;
}

interface ImageManagerProps {
  productId: number;
  images: ProductImage[];
  onImagesChange: () => void;
}

const ImageManager: React.FC<ImageManagerProps> = ({ productId, images, onImagesChange }) => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [draggedImageId, setDraggedImageId] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{show: boolean; id: number | null; name: string}>({show: false, id: null, name: ''});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sort images by sort_order for display
  const sortedImages = [...images].sort((a, b) => a.sort_order - b.sort_order);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress({});

    try {
      const uploadPromises = files.map(async (file, index) => {
        const progressKey = file.name;
        
        try {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('image_type', 'gallery');
          formData.append('alt_text', file.name);
          formData.append('is_primary', (images.length === 0 && index === 0).toString());

          const response = await api.post(`/products/${productId}/images/upload`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                setUploadProgress(prev => ({ ...prev, [progressKey]: progress }));
              }
            }
          });

          console.log(`${file.name} uploaded successfully:`, response.data);

        } catch (error) {
          console.error(`Error uploading ${file.name}:`, error);
          throw error;
        }
      });

      await Promise.all(uploadPromises);
      onImagesChange();
      
    } catch (error: any) {
      console.error('Error uploading images:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to upload images';
      alert(errorMsg);
    } finally {
      setUploading(false);
      setUploadProgress({});
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async (imageId: number) => {
    const image = images.find(img => img.id === imageId);
    setDeleteConfirm({show: true, id: imageId, name: image?.original_filename || image?.image_type || `Image #${imageId}`});
  };

  const handleDeleteImageConfirm = async () => {
    if (!deleteConfirm.id) return;
    try {
      await api.delete(`/images/${deleteConfirm.id}`);
      setDeleteConfirm({show: false, id: null, name: ''});
      onImagesChange();
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Failed to delete image');
    }
  };

  const handleSetPrimary = async (imageId: number) => {
    try {
      await api.put(`/products/${productId}/images/${imageId}/primary`);
      onImagesChange();
    } catch (error) {
      console.error('Error setting primary image:', error);
      alert('Failed to set primary image');
    }
  };

  const handleUpdateAltText = async (imageId: number, altText: string) => {
    try {
      await api.put(`/images/${imageId}`, { alt_text: altText });
      onImagesChange();
    } catch (error) {
      console.error('Error updating alt text:', error);
    }
  };

  // Drag and drop handlers for reordering
  const handleDragStart = (e: React.DragEvent, imageId: number) => {
    setDraggedImageId(imageId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    setDragOverIndex(null);
    
    if (!draggedImageId) return;

    const draggedIndex = sortedImages.findIndex(img => img.id === draggedImageId);
    if (draggedIndex === -1 || draggedIndex === dropIndex) {
      setDraggedImageId(null);
      return;
    }

    try {
      // Create new order array
      const newOrder = [...sortedImages];
      const [draggedImage] = newOrder.splice(draggedIndex, 1);
      newOrder.splice(dropIndex, 0, draggedImage);

      // Create reorder data with image IDs in new order
      const reorderData = newOrder.map(img => img.id);

      await api.put(`/products/${productId}/images/reorder`, reorderData);
      onImagesChange();
    } catch (error) {
      console.error('Error reordering images:', error);
      alert('Failed to reorder images');
    } finally {
      setDraggedImageId(null);
    }
  };

  return (
    <div>
      <h4>Product Images</h4>
      
      <div style={{ marginBottom: '1rem' }}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageUpload}
          accept="image/*"
          multiple
          style={{ display: 'none' }}
        />
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? 'Uploading...' : 'Add Images'}
        </button>
        {uploading && (
          <div style={{ marginTop: '8px', fontSize: '12px' }}>
            {Object.entries(uploadProgress).map(([filename, progress]) => (
              <div key={filename}>
                {filename}: {progress}%
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
        {sortedImages.map((image, index) => (
          <div 
            key={image.id} 
            draggable
            onDragStart={(e) => handleDragStart(e, image.id)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, index)}
            style={{ 
              border: image.is_primary ? '2px solid #007bff' : '1px solid #e2e8f0', 
              borderRadius: '8px', 
              padding: '8px',
              position: 'relative',
              cursor: 'move',
              backgroundColor: dragOverIndex === index ? '#f0f8ff' : 'white',
              transform: draggedImageId === image.id ? 'rotate(5deg)' : 'none',
              opacity: draggedImageId === image.id ? 0.7 : 1,
              transition: 'all 0.2s ease'
            }}
          >
            {image.is_primary && (
              <div style={{
                position: 'absolute',
                top: '4px',
                left: '4px',
                background: '#007bff',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: '10px',
                zIndex: 1
              }}>
                PRIMARY
              </div>
            )}
            
            {/* Sort order indicator */}
            <div style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              background: 'rgba(0,0,0,0.7)',
              color: 'white',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '10px',
              zIndex: 1
            }}>
              #{image.sort_order + 1}
            </div>
            
            <img
              src={image.image_url}
              alt={image.alt_text || image.original_filename}
              style={{ 
                width: '100%', 
                height: '150px', 
                objectFit: 'cover', 
                borderRadius: '4px', 
                cursor: 'pointer' 
              }}
              onClick={() => window.open(image.image_url, '_blank')}
              title="Click to view full size"
            />
            
            <div style={{ marginTop: '8px', fontSize: '12px' }}>
              <div><strong>Type:</strong> {image.image_type}</div>
              {image.file_size && (
                <div><strong>Size:</strong> {Math.round(image.file_size / 1024)}KB</div>
              )}
              {image.s3_key && (
                <div style={{ fontSize: '10px', color: '#666', wordBreak: 'break-all' }}>
                  <strong>S3:</strong> {image.s3_key.split('/').pop()}
                </div>
              )}
              
              <input
                type="text"
                placeholder="Alt text"
                defaultValue={image.alt_text || ''}
                onBlur={(e) => handleUpdateAltText(image.id, e.target.value)}
                style={{ 
                  width: '100%', 
                  marginTop: '4px', 
                  padding: '2px 4px', 
                  fontSize: '11px',
                  border: '1px solid #ddd',
                  borderRadius: '2px'
                }}
              />
              
              <div style={{ display: 'flex', gap: '4px', marginTop: '6px', flexWrap: 'wrap' }}>
                {!image.is_primary && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    style={{ fontSize: '10px', padding: '2px 6px' }}
                    onClick={() => handleSetPrimary(image.id)}
                  >
                    Set Primary
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-danger"
                  style={{ fontSize: '10px', padding: '2px 6px' }}
                  onClick={() => handleDeleteImage(image.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {sortedImages.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem', 
          color: '#666',
          border: '2px dashed #ddd',
          borderRadius: '8px'
        }}>
          No images uploaded yet. Click "Add Images" to upload product images.
          <br />
          <small style={{ fontSize: '11px', marginTop: '8px', display: 'block' }}>
            Drag and drop images to reorder them. The first image will be the primary image.
          </small>
        </div>
      )}
      <ConfirmDeleteModal
        isOpen={deleteConfirm.show}
        title="Delete Image"
        message="Are you sure you want to delete this image? This action cannot be undone."
        itemName={deleteConfirm.name}
        onConfirm={handleDeleteImageConfirm}
        onCancel={() => setDeleteConfirm({show: false, id: null, name: ''})}
      />
    </div>
  );
};

export default ImageManager;